import { useEffect, useState } from "react";

export const useWizardNavigation = (
  getStepConfig,
  canProceed,
  initialStep = 0,
  freeNavigation = false,
) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [stepHistory, setStepHistory] = useState(
    initialStep > 0
      ? Array.from({ length: initialStep + 1 }, (_, i) => i)
      : [0],
  );
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Update step if initialStep changes (for edit mode)
  useEffect(() => {
    if (initialStep > 0 && currentStep === 0) {
      setCurrentStep(initialStep);
      setStepHistory(Array.from({ length: initialStep + 1 }, (_, i) => i));
    }
  }, [initialStep]);

  const currentStepId = getStepConfig[currentStep]?.id || "instructions";
  const isFinalStep = currentStep === getStepConfig.length - 1;

  const handleNext = async () => {
    // Mark validation as attempted for current step
    if (currentStepId === "incident_overview") {
      setValidationAttempted(true);
    }

    // Check if we can proceed
    if (!canProceed(currentStepId)) {
      return; // Don't proceed if validation fails
    }

    const nextStep = currentStep + 1;
    if (nextStep < getStepConfig.length) {
      setStepHistory([...stepHistory, nextStep]);
      setCurrentStep(nextStep);
      setValidationAttempted(false); // Reset for next step
    }
  };

  // Advance to next step without validation (used after save success)
  const forceNext = () => {
    const nextStep = currentStep + 1;
    if (nextStep < getStepConfig.length) {
      setStepHistory([...stepHistory, nextStep]);
      setCurrentStep(nextStep);
      setValidationAttempted(false);
    }
  };

  const handleBack = () => {
    if (stepHistory.length > 1) {
      const newHistory = [...stepHistory];
      newHistory.pop();
      setStepHistory(newHistory);
      setCurrentStep(newHistory[newHistory.length - 1]);
    }
  };

  const handleStepClick = (targetIndex) => {
    const inRange = targetIndex >= 0 && targetIndex < getStepConfig.length;
    const canJump = freeNavigation
      ? inRange
      : inRange && (targetIndex <= currentStep || targetIndex === 0);
    if (!canJump || targetIndex === currentStep) return;

    setCurrentStep(targetIndex);
    setValidationAttempted(false);
    const newHistory = stepHistory.slice(0, targetIndex + 1);
    if (newHistory[newHistory.length - 1] !== targetIndex) {
      newHistory.push(targetIndex);
    }
    setStepHistory(newHistory.length ? newHistory : [targetIndex]);
  };

  // Navigate directly to a specific step (for resume functionality)
  const navigateToStep = (targetStep) => {
    if (targetStep >= 0 && targetStep < getStepConfig.length) {
      setCurrentStep(targetStep);
      setStepHistory(Array.from({ length: targetStep + 1 }, (_, i) => i));
    }
  };

  return {
    currentStep,
    stepHistory,
    currentStepId,
    isFinalStep,
    validationAttempted,
    handleNext,
    forceNext,
    handleBack,
    handleStepClick,
    navigateToStep,
  };
};
